import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Icon from "../custom-widgets/icon";


/**
 * Search Bar Component
 * 
 * @param {Object} props - The object containing props.
 * @param {string} props.id - The id of the search bar.
 * @param {string} props.idSuffix - The suffix to append to the id.
 * @param {string} props.label - The label for the search input.
 * @param {React.JSX.Element} props.customSearchIconElement - Optional custom search icon element.
 * @param {string} props.query - The current search query state.
 * @param {function} props.onClearQuery - Callback function called after we cleared the search input.
 * @param {function} props.onInputChange - Callback function to handle input changes.
 * @param {function} props.onPressEnter - Callback function to handle Enter key press.
 * @param {function} props.onSearchButtonClick - Callback function to handle search button click.
 * @param {function} props.onFocus - custom callback function to handle onFocus event of the input.
 * @param {boolean} props.shouldFocus -flag to determine if the search bar should be focused on render.
 * @param {function} props.shouldFocusCallback - callback function to be called after the first render after we focused if we had shouldFocus = true.
 * @param {string} props.containerClassName - additional class name for the container.
 * @param {Object} props.error - error object containing error message and other details, if not null, we change the style to red.
 * 
 * @returns {JSX.Element} The rendered SuggestionList component
 */
const SearchBar = ({
  id = "ssi",
  idSuffix = "",
  label = "Search",
  customSearchIconElement = null,
  query = "",
  onClearQuery = () => {},
  onInputChange = () => {},
  onPressEnter = () => {},
  onSearchButtonClick = () => {},
  onFocus = () => {},
  shouldFocus = false,
  shouldFocusCallback = () => {},
  containerClassName = "",
  error = null
}) => {
  const [focus, setFocus] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [searchButtonFocus, setSearchButtonFocus] = useState(false);
  const lastInteraction = useRef(null);
  const searchRef = useRef(null);
  const clear = (e) => {
    e.preventDefault();
    searchRef.current.focus();
    onInputChange({ target: { value: "" } });
    onClearQuery();
  };

  const searchInputId = `${id}-search-input${idSuffix ? `-${idSuffix}` : ""}`;
  const clearTextButtonId = `${id}-clear-text-button${idSuffix ? `-${idSuffix}` : ""}`;
  const searchButtonId = `${id}-search-button${idSuffix ? `-${idSuffix}` : ""}`;

  const defaultOnFocus = () => {
    setFocus(true);
    onFocus();
  };
  const defaultOnBlur = () => {
    setFocus(false);
    setFocusWithin(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      lastInteraction.current = "tab";
    }
  };

  const handleMouseDown = () => {
    lastInteraction.current = "mouse";
  };

  const handleFocus = () => {
    if (lastInteraction.current === "tab") {
      setFocusWithin(true);
    }

    defaultOnFocus();
    lastInteraction.current = null;
  };

  useEffect(() => {
    if (shouldFocus) {
      searchRef.current.focus();
      shouldFocusCallback();
    }
  }, [shouldFocus, shouldFocusCallback]);

  const TAB_INDEX = 0;

  return (
    <div
      className={`sb-input-container ${focus ? "sb-input-container-focused" : ""} ${
        focusWithin ? "sb-input-container-focus-within" : ""
      } ${containerClassName}`}
      style={error ? (focus ? { boxShadow: "0 0 0 2px #DA1E28 inset" } : { boxShadow: "0 0 0 1px #DA1E28 inset" }) : {}}
    >
      <input
        id={searchInputId}
        type="text"
        className="form-control sb-input"
        style={error ? { borderColor: "red" } : {}}
        aria-label={label}
        ref={searchRef}
        aria-describedby={"ssi-input-label"}
        value={query}
        onChange={onInputChange}
        onKeyDown={(e) => {
          handleKeyDown(e);
          if (e.key === "Enter") {
            e.preventDefault();
            onPressEnter(query);
          }
        }}
        onMouseDown={handleMouseDown}
        onFocus={handleFocus}
        onBlur={defaultOnBlur}
        label={label}
        tabIndex={TAB_INDEX}
      />
      <button
        id={clearTextButtonId}
        onClick={clear}
        onFocus={() => defaultOnFocus()}
        onBlur={() => defaultOnBlur()}
        className="sb-clear-text-button inner-focus-within"
        tabIndex={TAB_INDEX}
      >
        <Icon name="times" class="fa-md text-secondary" />
      </button>
      <button
        onClick={(event) => {
          event.preventDefault();
          onSearchButtonClick(query);
        }}
        className={`btn btn-primary no-min-width sb-search-button ${
          searchButtonFocus ? "sb-search-button-focused" : ""
        }`}
        style={error ? { background: "#DA1E28" } : {}}
        id={searchButtonId}
        onFocus={() => {
          defaultOnFocus();
          setSearchButtonFocus(true);
        }}
        onBlur={() => {
          defaultOnBlur();
          setSearchButtonFocus(false);
        }}
        tabIndex={TAB_INDEX}
      >
        {customSearchIconElement ? customSearchIconElement : <Icon name="search" class="fa-lg" />}
        <span className="sr-only">Search</span>
      </button>
    </div>
  );
};

export default SearchBar;

SearchBar.propTypes = {
  id: PropTypes.string.isRequired,
  idSuffix: PropTypes.string.isRequired,
  label: PropTypes.string,
  customSearchIconElement: PropTypes.string,
  query: PropTypes.string.isRequired,
  onClearQuery: PropTypes.string,
  onInputChange: PropTypes.string.isRequired,
  onPressEnter: PropTypes.string,
  onSearchButtonClick: PropTypes.string.isRequired,
  onFocus: PropTypes.string,
  shouldFocus: PropTypes.string,
  shouldFocusCallback: PropTypes.string,
  containerClassName: PropTypes.string,
  error: PropTypes.string,
};

